import { useCallback, useEffect, useState } from 'react'
import { prefetchWithRetry, type PrefetchOutcome } from '../../offline/prefetch'

export type PrefetchState =
  | { phase: 'prefetching'; attempt: number }
  | { phase: 'ready'; outcome: PrefetchOutcome }
  | { phase: 'unavailable'; error: string }

/**
 * The prefetch trigger point (§5.1): prefetch starts on **screen entry**, not on
 * the Start tap, and retries with backoff on its own (`prefetchWithRetry`).
 *
 * Leaving the screen, or switching descriptor, aborts the pending retry. The
 * state is tagged with the run it belongs to, so a result that arrives for an
 * older run is never shown for the current one.
 */
export function usePrefetch(descriptorId: string): { state: PrefetchState; retry(): void } {
  const [run, setRun] = useState(0)
  const key = `${descriptorId}#${run}`
  const [tagged, setTagged] = useState<{ key: string; state: PrefetchState } | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    const set = (state: PrefetchState): void => {
      if (!controller.signal.aborted) setTagged({ key, state })
    }

    prefetchWithRetry(descriptorId, {
      signal: controller.signal,
      onRetry: (attempt) => set({ phase: 'prefetching', attempt }),
    }).then(
      (outcome) => set({ phase: 'ready', outcome }),
      (error: unknown) => set({ phase: 'unavailable', error: error instanceof Error ? error.message : String(error) }),
    )

    return () => controller.abort()
  }, [descriptorId, key])

  const retry = useCallback(() => setRun((r) => r + 1), [])

  const state: PrefetchState = tagged?.key === key ? tagged.state : { phase: 'prefetching', attempt: 0 }
  return { state, retry }
}
